/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import afterSetExtremes from "components/charts/modern/highcharts/afterSetExtremes";
import darkNavigatorStyles from "components/charts/modern/highcharts/darkNavigatorStyles";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import type { AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { LogLinearScaleSelector } from "components/charts/modern/inputs/AxisScaleSelector";
import { LineBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import IssuersSelect from "components/charts/modern/overlay/IssuersSelect/IssuersSelect";
import SelectedIssuersStack from "components/charts/modern/overlay/IssuersSelect/SelectedIssuersStack";
import { SELECTED_ISSUERS_ATOM } from "components/charts/modern/overlay/IssuersSelect/atoms";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import MarketCapitalizationTooltip from "components/charts/modern/tooltip/MarketCapitalizationTooltip";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import Avatar from "components/core/Avatar";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { Button } from "components/ui/Button";
import { IsKiosking } from "components/ui/Kiosk";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import parseISO from "date-fns/parseISO";
import type Highcharts from "highcharts/highstock";
import type { SeriesLineOptions, SeriesColumnOptions } from "highcharts/highstock";
import { useAtomValue } from "jotai";
import MarketCapitalizationHistoryQuery from "lib/graphql/queries/MarketCapitalizationHistoryQuery";
import type {
  IMarketCapitalizationHistoryQuery,
  IMarketCapitalizationHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/MarketCapitalizationHistoryQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { useEffect, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import { IIssuerListingCountry } from "types/graphqlTypes";
import useTheme from "components/ui/useTheme";

interface MarketCapitalizationChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function MarketCapitalizationChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: MarketCapitalizationChartProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const issuer = useRootIssuer();
  const theme = useTheme();
  const [isShowingEnterpriseValue, setIsShowingEnterpriseValue] = useState(false);
  const [plotType, setPlotType] = useState<"line" | "column">("line");
  const [axisScaleType, setAxisScaleType] = useState<AxisScaleType>("linear");
  const selectedIssuers = useAtomValue(SELECTED_ISSUERS_ATOM);
  const tickerSymbolsForSeries = [...tickerSymbols].concat(selectedIssuers.map((node) => node.tickerSymbol));

  const { data, previousData, loading } = useQuery<
    IMarketCapitalizationHistoryQuery,
    IMarketCapitalizationHistoryQueryVariables
  >(MarketCapitalizationHistoryQuery, {
    variables: {
      tickerSymbols: [...new Set(tickerSymbolsForSeries)].slice(0, 5),
    },
  });
  const marketCapitalizationHistory =
    (loading && !data
      ? previousData?.marketCapitalizationHistoryByTickerSymbols
      : data?.marketCapitalizationHistoryByTickerSymbols) || [];

  useEffect(() => {
    if (isShowingEnterpriseValue && marketCapitalizationHistory.length > 1) {
      setIsShowingEnterpriseValue(false);
    }
  }, [marketCapitalizationHistory, isShowingEnterpriseValue]);

  const hasMarketCapitalizationHistory = marketCapitalizationHistory.every(({ nodes }) => nodes.length > 0);

  const options = getHighchartsOptions(
    marketCapitalizationHistory,
    axisScaleType,
    plotType,
    isShowingEnterpriseValue,
    intl,
    theme,
  );

  if (loading) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  const hasZeroValue = marketCapitalizationHistory
    .map((node) => node.nodes.some((node) => node.totalCapitalization === 0 || node.enterpriseValue === 0))
    .some((node) => node === true);

  return (
    <OnlyPremiumRender>
      <div className="flex h-full w-full flex-col space-y-4">
        <div className="flex flex-col items-center justify-between sm:flex-row">
          <div className="grid w-full grid-cols-3 items-center gap-4 md:flex md:space-y-0">
            {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
              <div className="col-span-3">
                <TickerSymbolSelector
                  tickerSymbols={availableTickerSymbols}
                  value={tickerSymbols}
                  onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
                />
              </div>
            )}

            <LineBarPlotTypeSelector value={plotType} onChange={setPlotType} />
            {!hasZeroValue && <LogLinearScaleSelector value={axisScaleType} onChange={setAxisScaleType} />}

            {issuer.__typename !== "BrazilianRealEstateFund" && (
              <Button
                variant="primary/green"
                disabled={marketCapitalizationHistory.length > 1}
                className="disabled:cursor-not-allowed"
                onClick={() => setIsShowingEnterpriseValue((previous) => !previous)}
              >
                EV
              </Button>
            )}

            {issuer.__typename === "AmericanCompanyIssuer" && (
              <IssuersSelect
                title={intl.formatMessage({ defaultMessage: "Pesquise empresas", id: "z9vMOi" })}
                description={intl.formatMessage({
                  defaultMessage: "Digite um ticker para realizar uma busca rápida...",
                  id: "g9lrUo",
                })}
                inputSearchPlaceholder={intl.formatMessage({
                  defaultMessage: "Por exemplo: AAPL ou NVDA",
                  id: "FuzpLZ",
                })}
                inputSearchNoResultsText={intl.formatMessage({ defaultMessage: "Nenhum resultado", id: "XpuAg7" })}
                issuerId={issuer._id}
                listingCountry={
                  issuer.__typename === "AmericanCompanyIssuer" ? IIssuerListingCountry.Us : IIssuerListingCountry.Br
                }
              >
                <Button
                  variant="secondary"
                  className="col-span-3 row-start-1 w-full disabled:pointer-events-auto disabled:cursor-not-allowed sm:max-w-xs lg:w-auto"
                  disabled={!(selfie && selfie.isPremium)}
                >
                  {selectedIssuers.length > 0 ? `Compare (${selectedIssuers.length})` : "Compare"}
                </Button>
              </IssuersSelect>
            )}
          </div>

          <div className="hidden items-center space-x-2 sm:inline-flex">
            <SimpleTextTooltip content={issuer.name}>
              <Avatar
                src={issuer.__typename !== "BrazilianRealEstateFund" && issuer.niceLogo ? issuer.niceLogo.url : null}
                hashValue={issuer._id}
                alt={issuer.name}
                size={40}
                squircle={true}
              />
            </SimpleTextTooltip>

            <SelectedIssuersStack isLoading={loading} />
          </div>
        </div>

        <IsKiosking>
          {(isKiosking) => {
            if (hasMarketCapitalizationHistory) {
              return (
                <HighchartsChart
                  key={`MarketCapitalizationChart-${marketCapitalizationHistory.length}}`}
                  options={options}
                  className="h-96 xl:h-full"
                />
              );
            }

            return (
              <div
                className={classNames("flex h-80 w-80 items-center justify-center sm:w-full", {
                  "h-[calc(theme('spacing.96')*1.25)]": isKiosking,
                })}
              >
                <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
                  <div>
                    <SadFaceIcon />
                  </div>

                  <p>
                    <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
                  </p>
                </div>
              </div>
            );
          }}
        </IsKiosking>
      </div>
    </OnlyPremiumRender>
  );
}

function getHighchartsOptions(
  data: IMarketCapitalizationHistoryQuery["marketCapitalizationHistoryByTickerSymbols"],
  axisScaleType: AxisScaleType,
  plotType: "line" | "column",
  isShowingEnterpriseValue: boolean,
  intl: IntlShape,
  currentTheme: "dark" | "light",
): Highcharts.Options {
  // Theme-aware colors
  const slateOrDarkSlate = currentTheme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = currentTheme === "dark" ? colors.slate[300] : colors.slate[500];
  const crosshairColor = currentTheme === "dark" ? colors.slate[500] : colors.slate[800];
  const tooltipBgColor = currentTheme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = currentTheme === "dark" ? colors.slate[950] : colors.slate[200];

  // Series colors for theme
  const lineColor = currentTheme === "dark" ? colors.indigo[400] : colors.indigo[600];
  const markerFillColor = currentTheme === "dark" ? colors.indigo[900] : colors.indigo[50];
  const columnColor = currentTheme === "dark" ? colors.indigo[700] : colors.indigo[300];
  const hoverBorderColor = currentTheme === "dark" ? colors.violet[400] : colors.violet[500];
  const hoverFillColor = currentTheme === "dark" ? colors.violet[900] : colors.violet[50];

  const baseOptions: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
      events: {
        afterSetExtremes: function (this, event) {
          afterSetExtremes(this, event);
        },
      },
    },
    rangeSelector: {
      enabled: false,
    },
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return renderToStaticMarkup(
          <MarketCapitalizationTooltip context={this} marketCapitalizationHistory={data} intl={intl} />,
        );
      },
    },
    plotOptions: {
      series: {
        turboThreshold: 0,
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: hoverBorderColor,
            color: hoverFillColor,
            lineWidth: 2,
          },
        },
      },
      line: {
        color: lineColor,
        lineWidth: 2,
        marker: {
          enabled: false,
          fillColor: markerFillColor,
          lineColor: lineColor,
          lineWidth: 2,
          width: 8,
          height: 8,
        },
      },
      column: {
        dataGrouping: {
          enabled: true,
          approximation: "average",
        },
        color: columnColor,
        borderWidth: 1.5,
        borderColor: columnColor,
        states: {
          hover: {
            borderColor: lineColor,
            color: lineColor,
          },
        },
      },
    },
  };

  if (data.length === 1) {
    const [history] = data;

    const labels = {
      totalCapitalization: intl.formatMessage({
        defaultMessage: "Valor de Mercado",
        id: "3JpRRP",
      }),
      enterpriseValue: intl.formatMessage({
        defaultMessage: "Valor Da Firma (EV)",
        id: "RoFZ2U",
      }),
    };

    const nodes = [
      {
        name: labels.totalCapitalization,
        data: history.nodes.map((node) => {
          return {
            x: parseISO(node.asOfDate).getTime(),
            y: node.totalCapitalization,
            custom: node,
          };
        }),
      },
      isShowingEnterpriseValue
        ? {
            name: labels.enterpriseValue,
            data: history.nodes.map((node) => {
              return {
                x: parseISO(node.asOfDate).getTime(),
                y: node.enterpriseValue,
                custom: node,
              };
            }),
          }
        : null,
    ].filter(notUndefinedOrNull);

    const series = nodes.map((node, index) => {
      const theme = getColorThemeForIndex(index, currentTheme);

      if (!theme) {
        throw new Error("Theme not found.");
      }

      const defaultOptions = {
        showInNavigator: true,
        type: plotType,
        animation: false,
        name: node.name,
        color: theme.lineColor,
        lineWidth: 2,
        data: node.data.sort((a, b) => compareAsc(a.x, b.x)),
        custom: {
          tickerSymbol: history.tickerSymbol,
          primaryColor: theme.color,
          secondaryColor: theme.lineColor,
        },
        marker: {
          width: 8,
          height: 8,
          lineWidth: 1,
          fillColor: theme.color,
          color: theme.color,
          lineColor: theme.lineColor,
          symbol: "circle",
        },
      };

      if (plotType === "column") {
        const columnOptions = {
          borderColor: theme.lineColor,
          borderWidth: 1.5,
          states: {
            hover: {
              color: theme.lineColor,
            },
          },
        };
        return { ...columnOptions, ...defaultOptions };
      }

      return defaultOptions;
    }) as (SeriesLineOptions | SeriesColumnOptions)[];

    const navigatorStyle = currentTheme === "dark" ? darkNavigatorStyles : navigatorStyles;

    return {
      ...baseOptions,
      series: [...series],
      yAxis: {
        type: axisScaleType,
        gridLineColor: slateOrDarkSlate,
        lineWidth: 1,
        lineColor: slateOrDarkSlate,
        labels: {
          enabled: true,
          align: "center",
          style: {
            color: labelsColor,
            fontFamily: aeonik.style.fontFamily,
            fontWeight: "500",
            fontSize: "12px",
          },
          formatter: function () {
            const valueFormatter = new Intl.NumberFormat("en", {
              notation: "compact",
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            });
            return valueFormatter.format(parseFloat(String(this.value)));
          },
        },
        tickWidth: 0,
        showFirstLabel: true,
        showLastLabel: false,
        scrollbar: {
          enabled: false,
        },
        crosshair: {
          snap: true,
          dashStyle: "Dash",
          color: crosshairColor,
        },
      },
      navigator: {
        ...navigatorStyle,
        yAxis: {
          ...navigatorStyle.yAxis,
          events: {
            afterSetExtremes(this, event) {
              this.setExtremes(event.dataMin, event.dataMax);
            },
          },
          type: axisScaleType,
        },
      },
    };
  }

  const series: (SeriesLineOptions | SeriesColumnOptions)[] = data.map((series, index) => {
    const theme = getColorThemeForIndex(index, currentTheme);

    if (!theme) {
      throw new Error("Theme not found.");
    }

    const defaultOptions: SeriesLineOptions | SeriesColumnOptions = {
      showInNavigator: true,
      type: plotType === "line" ? "line" : "column",
      animation: false,
      name: series.tickerSymbol,
      color: theme ? theme.lineColor : undefined,
      lineWidth: 2,
      data: series.nodes
        .map((node) => {
          return {
            x: parseISO(node.asOfDate).getTime(),
            y: node.totalCapitalization,
            custom: node,
          };
        })
        .sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: series.tickerSymbol,
        primaryColor: theme.color,
        secondaryColor: theme.lineColor,
      },
      marker: {
        width: 8,
        height: 8,
        lineWidth: 1,
        fillColor: theme.color,
        color: theme.color,
        lineColor: theme.lineColor,
        symbol: "circle",
      },
    };

    if (plotType === "column") {
      const columnOptions = {
        borderColor: theme ? theme.lineColor : undefined,
        borderWidth: 1.5,
        states: {
          hover: {
            color: theme ? theme.lineColor : undefined,
          },
        },
      };
      return { ...columnOptions, ...defaultOptions };
    }

    return defaultOptions;
  });

  const navigatorStyle = currentTheme === "dark" ? darkNavigatorStyles : navigatorStyles;

  return {
    ...baseOptions,
    yAxis: {
      type: axisScaleType,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        formatter: function () {
          return intl.formatNumber(parseFloat(String(this.value)), {
            notation: "compact",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
        },
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickWidth: 0,
      showFirstLabel: false,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
    },
    navigator: {
      ...navigatorStyle,
      yAxis: {
        ...navigatorStyle.yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        type: axisScaleType,
      },
    },
    series: [...series],
  };
}
